import {
  FormControlLabel,
  InputAdornment,
  LinearProgress,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import {
  GridColDef,
  GridColumnVisibilityModel,
  GridSortModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
} from "@mui/x-data-grid";
import dayjs from "dayjs";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useDebounce } from "use-debounce";

import { ServerDataGrid, usePagination, useSorting } from "components/ui/ServerDataGrid";
import { FDS_SubscriptionTypes } from "constants/index";
import { useAppDispatch, useAuth, useFactsetStreaming } from "features/store";
import { useListSecuritiesQuery } from "features/trading/accounts";
import { Security, SecurityKind } from "helpers/types";
import { useFDSWebSocket } from "hooks/useFDSWebSocket";
import { fdsSubKey, resolvePrice } from "./securityPrices";

const TICKER_DEBOUNCE_MS = 300;

// The grid shows each classification FK by name, so it sorts on the name too.
const SORT_FIELDS: Record<string, string> = {
  asset_category: "asset_category__name",
  asset_class: "asset_class__name",
  risk_category: "risk_category__name",
  product_type: "product_type__name",
  product_sub_type: "product_sub_type__name",
};

const toServerSorting = (sorting: GridSortModel): GridSortModel =>
  sorting.map((item) => ({
    ...item,
    field: SORT_FIELDS[item.field] ?? item.field,
  }));

const formatDate = (value: string | null, format: string) =>
  value ? dayjs(value).format(format) : "";

const PRICE_FORMAT = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

const ToolBar = () => (
  <GridToolbarContainer>
    <GridToolbarColumnsButton />
    <GridToolbarDensitySelector />
  </GridToolbarContainer>
);

const buildColumns = (
  kind: SecurityKind,
  fdsCache: Record<string, any> | undefined,
): GridColDef<Security>[] => [
  { field: "symbol", headerName: "Ticker", width: kind === "option" ? 210 : 110 },
  { field: "name", headerName: "Name", flex: 1, minWidth: 320 },
  { field: "asset_category", headerName: "Asset Category", width: 140 },
  { field: "asset_class", headerName: "Asset Class", width: 180 },
  { field: "risk_category", headerName: "Risk Category", width: 150 },
  { field: "product_type", headerName: "Product Type", width: 120 },
  { field: "product_sub_type", headerName: "Product Sub-Type", width: 150 },
  { field: "orion_product_id", headerName: "Orion Product ID", width: 150 },
  // Stocks do not mature: the maturity date is the options table's only.
  ...(kind === "option"
    ? [
        {
          field: "maturity_date",
          headerName: "Maturity Date",
          width: 130,
          valueFormatter: ({ value }: { value: string | null }) =>
            formatDate(value, "MM/DD/YYYY"),
        },
      ]
    : []),
  {
    field: "last_price",
    headerName: "Last Price",
    // The server sorts on the stored price; a FactSet price shown in its
    // place does not move the row.
    description: "Sorts by the last price stored from the Orion import",
    width: 130,
    type: "number",
    renderCell: ({ row }) => {
      const price = resolvePrice(row, fdsCache);
      if (price.source === "db" && price.value === 0) {
        return (
          <Tooltip title="No price on record">
            <span>—</span>
          </Tooltip>
        );
      }
      return (
        <Tooltip
          title={
            price.source === "fds"
              ? "Last price from the FactSet feed"
              : "Last price from the Orion import"
          }
        >
          <span>{PRICE_FORMAT.format(price.value)}</span>
        </Tooltip>
      );
    },
  },
  {
    field: "created_on",
    headerName: "Created On",
    width: 150,
    valueFormatter: ({ value }) => formatDate(value, "MM/DD/YYYY HH:mm"),
  },
  { field: "created_by", headerName: "Created By", width: 110 },
  {
    field: "updated_on",
    headerName: "Updated On",
    width: 150,
    valueFormatter: ({ value }) => formatDate(value, "MM/DD/YYYY HH:mm"),
  },
  { field: "updated_by", headerName: "Updated By", width: 110 },
];

export default function SecurityMaster() {
  const { isAuthenticated } = useAuth();
  const dispatch = useAppDispatch();

  const [kind, setKind] = useState<SecurityKind>("stock");
  const [ticker, setTicker] = useState("");
  const [debouncedTicker] = useDebounce(ticker.trim(), TICKER_DEBOUNCE_MS);
  const [sorting, setSorting] = useSorting("symbol");
  const [pagination, setPagination] = usePagination(25);
  // Controlled, so a column choice survives switching between the two tables.
  const [columnVisibility, setColumnVisibility] =
    useState<GridColumnVisibilityModel>({});

  const resetPage = () => setPagination((current) => ({ ...current, page: 0 }));

  const onKindChange = (event: ChangeEvent<HTMLInputElement>) => {
    setKind(event.target.value as SecurityKind);
    resetPage();
  };

  const onTickerChange = (event: ChangeEvent<HTMLInputElement>) => {
    setTicker(event.target.value);
  };

  // On the debounced value, so a keystroke does not fetch page 0 of the
  // filter it is about to replace.
  useEffect(resetPage, [debouncedTicker]);

  const { data, currentData, isFetching } = useListSecuritiesQuery(
    {
      kind,
      ticker: debouncedTicker || undefined,
      pagination,
      sorting: toServerSorting(sorting),
    },
    { skip: !isAuthenticated },
  );
  // `data` outlives a change of arguments, which keeps a page turn from
  // flashing empty -- but across a switch of table it is the OTHER table's
  // rows, so it is shown only once the current table has answered.
  const [dataKind, setDataKind] = useState<SecurityKind | undefined>();
  useEffect(() => {
    if (currentData) {
      setDataKind(kind);
    }
  }, [currentData, kind]);
  const rows = useMemo(
    () => (dataKind === kind ? data?.results ?? [] : []),
    [data, dataKind, kind],
  );

  // The last price, not a stream: the page's rows are subscribed so the FDS
  // gateway's in-memory price can stand in for the stored one where it has one.
  const { subscriptions, dataCache } = useFactsetStreaming();
  // From `currentData`, not the rows on screen: while a switch of table is in
  // flight those are still the other table's, and would be subscribed as the
  // wrong FDS type.
  const subKey = useMemo(
    () => fdsSubKey(kind, currentData?.results ?? []),
    [kind, currentData],
  );
  useFDSWebSocket(
    kind === "stock" ? subKey : null,
    kind === "option" ? subKey : null,
    subscriptions,
    undefined,
    dispatch,
  );
  const fdsCache =
    dataCache[
      kind === "option"
        ? FDS_SubscriptionTypes.HOMEPAGE_OPTION
        : FDS_SubscriptionTypes.HOMEPAGE_STOCK
    ];

  const columns = useMemo(() => buildColumns(kind, fdsCache), [kind, fdsCache]);

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1}
      >
        <RadioGroup row value={kind} onChange={onKindChange} aria-label="Security type">
          <FormControlLabel value="stock" control={<Radio />} label="Stocks" />
          <FormControlLabel value="option" control={<Radio />} label="Options" />
        </RadioGroup>
        <TextField
          size="small"
          label="Filter by ticker"
          value={ticker}
          onChange={onTickerChange}
          sx={{ width: { xs: "100%", sm: 260 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Stack>
      <ServerDataGrid
        autoHeight
        density="compact"
        slots={{ toolbar: ToolBar, loadingOverlay: LinearProgress }}
        disableColumnFilter
        rows={rows}
        rowCount={data?.count ?? 0}
        loading={isFetching}
        columns={columns}
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        sortModel={sorting}
        onSortModelChange={(model) => {
          setSorting(model);
          resetPage();
        }}
        columnVisibilityModel={columnVisibility}
        onColumnVisibilityModelChange={setColumnVisibility}
        disableRowSelectionOnClick
      />
    </Stack>
  );
}
